import type { PrayerDay } from "./types";
import type { NextPrayerInfo, PrayerKey } from "./nextPrayer";
import { sunProgress } from "./solar";

export type PrayerPhase = "fajr" | "dhuhr" | "asr" | "maghrib" | "isha";

export type AtmosphereTheme = {
  /** Primary ambience tint in rgb() numeric triplet form for CSS usage. Example: "180 240 255" */
  tintRGB: string;
  /** Secondary tint for depth. */
  tint2RGB: string;
  /** Subtle highlight tint used for ring/glow accents. */
  accentRGB: string;
};

export type PrayerPhaseState = {
  phase: PrayerPhase;
  /** 0..1 progress within the current prayer window (or toward next prayer for pre‑Fajr). */
  phaseProgress: number;
  /** 0..1 day progress (solar proxy). */
  dayProgress: number;
  /** 0..1 calm animation intensity (lower at night). */
  animationIntensity: number;
  theme: AtmosphereTheme;
  sun: {
    progress01: number;
    label: "Night" | "Dawn" | "Day" | "Afternoon" | "Twilight";
  };
  reminder: string;
};

function clamp01(n: number) {
  return Math.max(0, Math.min(1, n));
}

function parseHHMM(s: string): { hh: number; mm: number } | null {
  const m = /^\s*(\d{1,2}):(\d{2})/.exec(String(s));
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  return { hh, mm };
}

function isoToYMD(iso: string): { y: number; m: number; d: number } {
  const [y, m, d] = iso.split("-").map((x) => Number(x));
  return { y, m, d };
}

/**
 * Convert a wall-clock time (in the given timezone's calendar day) to a stable UTC epoch
 * using the same technique as nextPrayer.ts: represent wall-clock as Date.UTC.
 */
function timeEpochMsForISODate(timeZone: string, dateISO: string, hhmm: string): number | null {
  void timeZone; // timezone already baked into the provided day schedule
  const t = parseHHMM(hhmm);
  if (!t) return null;
  const { y, m, d } = isoToYMD(dateISO);
  return Date.UTC(y, m - 1, d, t.hh, t.mm, 0);
}

function phaseTheme(phase: PrayerPhase): AtmosphereTheme {
  // Calm, non-flashy tints aligned with the AkhirahProjects light palette.
  // These are used as *ambient light* (not background fills), so keep them soft.
  switch (phase) {
    case "fajr":
      // cool dawn over warm parchment
      return { tintRGB: "192 225 246", tint2RGB: "214 230 222", accentRGB: "200 235 222" };
    case "dhuhr":
      // soft daylight (almost neutral)
      return { tintRGB: "232 244 238", tint2RGB: "244 238 224", accentRGB: "198 232 220" };
    case "asr":
      // warm afternoon sand
      return { tintRGB: "245 232 206", tint2RGB: "226 240 232", accentRGB: "196 228 214" };
    case "maghrib":
      // gentle amber twilight
      return { tintRGB: "250 224 194", tint2RGB: "216 222 238", accentRGB: "232 202 152" };
    case "isha":
    default:
      // deep calm night (still light UI; ambience shifts cooler)
      return { tintRGB: "210 220 238", tint2RGB: "232 236 246", accentRGB: "190 220 232" };
  }
}

function reminderFor(phase: PrayerPhase): string {
  switch (phase) {
    case "fajr":
      return "Quiet dawn. Let the heart wake gently before the day begins.";
    case "dhuhr":
      return "A pause in the middle of life. Return to what matters.";
    case "asr":
      return "The afternoon passes quickly. Anchor yourself before time slips.";
    case "maghrib":
      return "As light fades, let gratitude rise — end the day with presence.";
    case "isha":
    default:
      return "Night calm. Release the day and leave your worries with Allah.";
  }
}

function sunLabel(sun01: number): PrayerPhaseState["sun"]["label"] {
  const s = clamp01(sun01);
  // Rough segments: 0/1 night, low dawn, mid day, late afternoon, twilight.
  if (s < 0.08 || s > 0.92) return "Night";
  if (s < 0.18) return "Dawn";
  if (s < 0.62) return "Day";
  if (s < 0.82) return "Afternoon";
  return "Twilight";
}

function intensityFor(phase: PrayerPhase, phaseProgress: number, sun01: number): number {
  // Keep everything subtle. Slightly more "alive" near transitions and daylight.
  const daylight = 0.25 + 0.55 * (1 - Math.abs(0.5 - clamp01(sun01)) * 2); // peaks midday
  const edge = 0.6 + 0.4 * Math.sin(clamp01(phaseProgress) * Math.PI); // gentle peak mid-window
  const base =
    phase === "isha" ? 0.25 : phase === "fajr" ? 0.35 : phase === "maghrib" ? 0.38 : 0.45;
  return clamp01(base * 0.6 + daylight * 0.25 + edge * 0.15);
}

function derivePhase(info: NextPrayerInfo | null): PrayerPhase {
  if (!info) return "dhuhr";
  const cur = info.current?.key;
  // Pre-Fajr: current is undefined, next is Fajr.
  if (!cur) return info.next.key === "fajr" ? "fajr" : "isha";
  return cur;
}

export function computePrayerPhaseState(params: {
  day: PrayerDay;
  nextDay?: PrayerDay | null;
  timeZone: string;
  info: NextPrayerInfo | null;
}): PrayerPhaseState {
  const { day, nextDay, timeZone, info } = params;

  const sun01 = sunProgress(day, timeZone);
  const phase = derivePhase(info);

  // Phase progress:
  // - If we have a current prayer time (info.current) we can compute window progress using schedule epochs.
  // - If pre‑Fajr, we treat progress toward Fajr over a soft 3h window for smooth ambience.
  let phaseProgress = 0;

  if (info?.current?.key) {
    const curKey = info.current.key as PrayerKey;
    const nextKey = info.next.key as PrayerKey;

    const curAt = (day.timings as any)[curKey] as string | undefined;
    const curEpoch = curAt ? timeEpochMsForISODate(timeZone, day.dateGregorianISO, curAt) : null;

    let nextEpoch: number | null = null;
    const nextDayISO = info.next.dateGregorianISO;
    if (nextDay && nextDayISO === nextDay.dateGregorianISO) {
      const at = (nextDay.timings as any)[nextKey] as string | undefined;
      nextEpoch = at ? timeEpochMsForISODate(timeZone, nextDay.dateGregorianISO, at) : null;
    } else {
      const at = (day.timings as any)[nextKey] as string | undefined;
      nextEpoch = at ? timeEpochMsForISODate(timeZone, day.dateGregorianISO, at) : null;
    }

    // Now epoch in the same stable wall-clock-as-UTC sense.
    // We use the same technique as nextPrayer.ts: rebuild "now" via Intl parts.
    // For simplicity here, approximate using Date() plus the countdown:
    // now = nextEpoch - inSeconds. This is stable given info.next.inSeconds comes from same epoch basis.
    const nowEpoch = nextEpoch != null ? nextEpoch - info.next.inSeconds * 1000 : null;

    if (curEpoch != null && nextEpoch != null && nowEpoch != null && nextEpoch > curEpoch) {
      phaseProgress = clamp01((nowEpoch - curEpoch) / (nextEpoch - curEpoch));
    } else {
      phaseProgress = clamp01(1 - Math.min(1, info.next.inSeconds / (60 * 60 * 6))); // fallback
    }
  } else if (info) {
    // Pre‑Fajr (or missing current): progress toward next prayer over a calm window.
    const windowSec = 60 * 60 * 3; // 3 hours
    phaseProgress = clamp01(1 - Math.min(1, info.next.inSeconds / windowSec));
  }

  const theme = phaseTheme(phase);
  const animationIntensity = intensityFor(phase, phaseProgress, sun01);

  return {
    phase,
    phaseProgress,
    dayProgress: clamp01(sun01),
    animationIntensity,
    theme,
    sun: { progress01: clamp01(sun01), label: sunLabel(sun01) },
    reminder: reminderFor(phase)
  };
}
